// Appointments (modules-and-api.md §5.7; INT-78..INT-85; BR-32) and the Zoho Bookings read adapter, in the shape
// docs/use-cases/appointments.md builds them (BR-APPOINTMENTS-001..021; D23, D37, D53; VX-23). Bookings owns the slot; Vision X
// holds the appointment record and its statuses. The adapter pulls each mapped branch's workspace into the appointment table; a
// booking matches a patient by exact name plus email or mobile (D37) and anything else waits in the inbox. Native slot
// allocation (INT-81, BranchCalendar) is the native-booking phase: /reschedule and /slots answer FEATURE_DISABLED.
import { z } from "zod";
import { PATIENT_CONSENT_STANDINGS, UserRefSchema } from "./governance";
import { CreatePatientInputSchema, foldName, normaliseEmail, normaliseMobile } from "./patients";
import { AuditStampSchema, InstantSchema, LocalDateSchema, UlidSchema } from "./primitives";
import { APPOINTMENT_STATUS_MACHINE, APPOINTMENT_STATUSES, PATIENT_STATUSES, type AppointmentStatus } from "./status-machines";

// --- Enumerations ------------------------------------------------------------------------------------------------------------

/** INT-78's three plus `bookings` for a pulled record (BR-APPOINTMENTS-010). Sprint 2 creates only `walk_in` here (BR-APPOINTMENTS-001). */
export const APPOINTMENT_SOURCES = ["staff", "walk_in", "online", "bookings"] as const;
export type AppointmentSource = (typeof APPOINTMENT_SOURCES)[number];
export const APPOINTMENT_STATUS_LABELS: Readonly<Record<AppointmentStatus, string>> = {
  Scheduled: "Scheduled", Confirmed: "Confirmed", Arrived: "Arrived", InProgress: "In progress", Completed: "Completed",
  Cancelled: "Cancelled", NoShow: "No-show", Rescheduled: "Rescheduled",
};
/** §5.7: the transition routes `POST /appointments/{id}/{segment}` and the status each one sets (BR-APPOINTMENTS-002). */
export const APPOINTMENT_TRANSITIONS: readonly { segment: string; to: AppointmentStatus }[] = [
  { segment: "confirm", to: "Confirmed" }, { segment: "arrive", to: "Arrived" }, { segment: "start", to: "InProgress" },
  { segment: "complete", to: "Completed" }, { segment: "cancel", to: "Cancelled" }, { segment: "no-show", to: "NoShow" },
];
/** BR-APPOINTMENTS-009: the transitions pushed back to Bookings. */
export const PUSHED_TRANSITIONS: readonly AppointmentStatus[] = ["Completed", "Cancelled", "NoShow"];
export const BOOKINGS_INBOX_STATUSES = ["Unmatched", "Linked", "Created", "Ignored"] as const;
export type BookingsInboxStatus = (typeof BOOKINGS_INBOX_STATUSES)[number];
export const BOOKINGS_INBOX_REASONS = ["NoMatch", "Ambiguous", "PatientArchived"] as const;
export type BookingsInboxReason = (typeof BOOKINGS_INBOX_REASONS)[number];
export const BOOKINGS_SYNC_TRIGGERS = ["schedule", "manual"] as const;
export const BOOKINGS_WINDOW_KINDS = ["near", "deep"] as const;
export const BOOKINGS_SYNC_OUTCOMES = ["Running", "Succeeded", "Failed", "Skipped"] as const;
/** BR-APPOINTMENTS-009: `not_required` while nothing is owed to Bookings (a pulled record, or a transition Bookings has no verb for). */
export const BOOKINGS_PUSH_STATES = ["not_required", "pending", "synced", "failed"] as const;
export type BookingsPushState = (typeof BOOKINGS_PUSH_STATES)[number];
/** BR-APPOINTMENTS-004 and UC-APPOINTMENTS-002 E2: what a pull refused to apply. */
export const BOOKINGS_CONFLICT_KINDS = ["status", "reschedule", "end_time_mismatch", "staff_changed"] as const;
/** BR-APPOINTMENTS-008: the counts every run records, in the view's field names. */
export const BOOKINGS_RUN_COUNT_FIELDS = ["fetched", "created", "updated", "unchanged", "rescheduled", "inbox", "ignored", "skipped", "conflicts", "unmappedStaff", "unmappedServices", "pushed", "pushFailures"] as const;
export const APPOINTMENT_DURATION_MIN = 5;
export const APPOINTMENT_DURATION_MAX = 600;
/** BR-APPOINTMENTS-007: the near window is yesterday to seven days ahead, the deep window to sixty days ahead. */
export const BOOKINGS_NEAR_WINDOW_DAYS = 7;
export const BOOKINGS_DEEP_WINDOW_DAYS = 60;
/** BR-APPOINTMENTS-012: the calendar read covers at most a week; a list's date range at most 62 days. */
export const CALENDAR_MAX_DAYS = 7;
export const APPOINTMENT_LIST_MAX_DAYS = 62;
/** The default reminder offsets (INT-83): 24 hours, and an optional 2 hours (Sprint 6). */
export const REMINDER_OFFSETS_MINUTES = { default: 1440, optional: 120 } as const;
/** BR-APPOINTMENTS-009: after this many failed pushes the appointment is flagged for staff. */
export const BOOKINGS_PUSH_FAILURE_FLAG = 5;

/** BR-APPOINTMENTS-016's seed list per tenant at provisioning; tenants add their own and name their Bookings services. */
export const SEEDED_APPOINTMENT_TYPES: readonly { code: string; name: string; defaultDurationMinutes: number; requiresOptometrist: boolean; recallIntervalMonths: number | null; isRecallSource: boolean; requiresDriversLicence: boolean; sortOrder: number }[] = [
  { code: "eye_test", name: "EyeTest", defaultDurationMinutes: 30, requiresOptometrist: true, recallIntervalMonths: 12, isRecallSource: true, requiresDriversLicence: false, sortOrder: 10 },
  { code: "drivers_licence_screening", name: "DriversLicenseScreening", defaultDurationMinutes: 20, requiresOptometrist: true, recallIntervalMonths: null, isRecallSource: false, requiresDriversLicence: true, sortOrder: 20 },
  { code: "new_glasses", name: "NewGlasses", defaultDurationMinutes: 30, requiresOptometrist: false, recallIntervalMonths: null, isRecallSource: false, requiresDriversLicence: false, sortOrder: 30 },
  { code: "prescription_sunglasses", name: "PrescriptionSunglasses", defaultDurationMinutes: 30, requiresOptometrist: false, recallIntervalMonths: null, isRecallSource: false, requiresDriversLicence: false, sortOrder: 40 },
  { code: "contact_lenses", name: "ContactLenses", defaultDurationMinutes: 30, requiresOptometrist: true, recallIntervalMonths: 12, isRecallSource: true, requiresDriversLicence: false, sortOrder: 50 },
  { code: "fitting_collection", name: "Fitting/Collection", defaultDurationMinutes: 15, requiresOptometrist: false, recallIntervalMonths: null, isRecallSource: false, requiresDriversLicence: false, sortOrder: 60 },
  { code: "follow_up", name: "Follow-up", defaultDurationMinutes: 15, requiresOptometrist: true, recallIntervalMonths: null, isRecallSource: false, requiresDriversLicence: false, sortOrder: 70 },
];
/** The type an unmapped Bookings service imports as when the branch mapping names none (UC-APPOINTMENTS-001 step 2). */
export const DEFAULT_UNMAPPED_TYPE_CODE = "follow_up";
/** D63: the longest booking-page URL an Admin may type on the Bookings mapping. */
export const BOOKING_PAGE_URL_MAX = 500;
/**
 * Today's worklist counts (`dependentsTurned18`) are counted up to this many; a count equal to it means at least this many
 * (the line reads "100+" and its link opens the full worklist).
 */
export const TODAY_COUNT_CAP = 100;

const text = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();
const Limit = z.coerce.number().int().min(1).max(200).default(50);
const Flag = z.enum(["true", "false"]);
const Duration = z.number().int().min(APPOINTMENT_DURATION_MIN).max(APPOINTMENT_DURATION_MAX);
const ColourHex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "#RRGGBB");
/** A Bookings service name as a tenant types it; compared ignoring case and surrounding spaces, a run of spaces read as one — a space still counts, so "EyeTest" is not "Eye Test" (BR-APPOINTMENTS-016). */
const ServiceName = z.string().trim().min(1).max(120);
export const foldServiceName = (s: string): string => s.replace(/\s+/g, " ").trim().toLowerCase();
const serviceNamesIssues = (names: string[] | undefined, ctx: z.RefinementCtx): void => {
  if (!names) return;
  const folded = names.map(foldServiceName);
  const dup = folded.findIndex((n, i) => folded.indexOf(n) !== i);
  if (dup >= 0) ctx.addIssue({ code: "custom", path: ["serviceNames", dup], message: "a service name appears more than once", params: { code: "duplicate" } });
};

// --- Appointment types (§5.7 /appointment-types; INT-79, INT-85, BR-APPOINTMENTS-016) ---------------------------------------

export const AppointmentTypeViewSchema = z.object({
  id: UlidSchema,
  code: z.string(),
  name: z.string(),
  defaultDurationMinutes: z.number().int(),
  colourHex: z.string().nullable(),
  requiresOptometrist: z.boolean(),
  /** Null = never generates a recall (INT-85). */
  recallIntervalMonths: z.number().int().nullable(),
  isRecallSource: z.boolean(),
  requiresDriversLicence: z.boolean(),
  /** The Bookings services that import as this type. */
  serviceNames: z.array(z.string()),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type AppointmentTypeView = z.infer<typeof AppointmentTypeViewSchema>;
export const AppointmentTypeInputSchema = z.strictObject({
  code: z.string().trim().regex(/^[a-z][a-z0-9_]{1,39}$/, "a lower-case code such as eye_test"),
  name: text(80).min(1),
  defaultDurationMinutes: Duration.default(30),
  colourHex: ColourHex.nullable().optional(),
  requiresOptometrist: z.boolean().optional(),
  recallIntervalMonths: z.number().int().min(1).max(120).nullable().optional(),
  isRecallSource: z.boolean().optional(),
  requiresDriversLicence: z.boolean().optional(),
  serviceNames: z.array(ServiceName).max(50).optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
}).superRefine((v, ctx) => serviceNamesIssues(v.serviceNames, ctx));
export type AppointmentTypeInput = z.infer<typeof AppointmentTypeInputSchema>;
/** PATCH /appointment-types/{id}. A type in use is deactivated, never deleted. */
export const AppointmentTypePatchSchema = z.strictObject({
  name: text(80).min(1).optional(),
  defaultDurationMinutes: Duration.optional(),
  colourHex: ColourHex.nullable().optional(),
  requiresOptometrist: z.boolean().optional(),
  recallIntervalMonths: z.number().int().min(1).max(120).nullable().optional(),
  isRecallSource: z.boolean().optional(),
  requiresDriversLicence: z.boolean().optional(),
  serviceNames: z.array(ServiceName).max(50).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
}).superRefine((v, ctx) => {
  if (Object.values(v).every((x) => x === undefined)) ctx.addIssue({ code: "custom", path: [], message: "nothing to change", params: { code: "empty" } });
  serviceNamesIssues(v.serviceNames, ctx);
});
export type AppointmentTypePatch = z.infer<typeof AppointmentTypePatchSchema>;

// --- The appointment record (§5.7; INT-78, INT-80, INT-82; BR-APPOINTMENTS-002, -010, -015) ---------------------------------

/** BR-APPOINTMENTS-010: the Bookings reference a pulled record carries; null for a walk-in. */
export const AppointmentBookingsRefSchema = z.object({
  bookingId: z.string(),
  workspaceId: z.string(),
  staffId: z.string().nullable(),
  serviceName: z.string().nullable(),
  /** The last status Bookings reported, verbatim. */
  remoteStatus: z.string().nullable(),
  lastPulledAt: InstantSchema.nullable(),
  /** BR-APPOINTMENTS-009. */
  pushState: z.enum(BOOKINGS_PUSH_STATES),
  pushError: z.string().nullable(),
  pushFailures: z.number().int(),
  /** Every push attempt, failed or not; after BOOKINGS_PUSH_FAILURE_FLAG failures the appointment is flagged for staff. */
  pushAttempts: z.number().int(),
  pushFlagged: z.boolean(),
  pushedAt: InstantSchema.nullable(),
  /** BR-APPOINTMENTS-004: a Bookings change Vision X refused, when there is one. */
  conflict: z.object({
    kind: z.enum(BOOKINGS_CONFLICT_KINDS),
    remoteStatus: z.string().nullable(),
    startAt: InstantSchema.nullable(),
    /** The `end_time` Bookings sent, verbatim, when it disagreed with start plus duration. */
    endTime: z.string().nullable(),
    staffId: z.string().nullable(),
    seenAt: InstantSchema,
  }).nullable(),
  /** A reschedule keeps the booking id on the new appointment; the old one shows it as history (UC-APPOINTMENTS-005). */
  current: z.boolean(),
  /**
   * The booking's own page in Zoho Bookings (the `summary_url` a pull brought), for "Open in Bookings"; null when Bookings sent
   * none. Where to book again is the appointment's `bookingsUrl` (D63). Additive (23 Sep 2026): absent in an older answer.
   */
  summaryUrl: z.string().nullable().default(null),
});
export type AppointmentBookingsRef = z.infer<typeof AppointmentBookingsRefSchema>;
/** BR-APPOINTMENTS-015: the banner and the prompt, computed from the patient's current status at read time. */
export const AppointmentWarningSchema = z.object({
  code: z.enum(["patient_prohibited", "patient_dormant", "main_account_prohibited"]),
  message: z.string(),
  reasonCode: z.string().nullable(),
  /** The status reason as typed (PAT-53); the main account's for a dependant (PAT-57). */
  reason: z.string().nullable(),
  /** The account the warning is about: the patient's, or the main account's when the warning comes through it. */
  accountCode: z.string(),
  viaMainAccount: z.boolean(),
  /** `reactivate` on a Dormant patient's check-in (PAT-50); the reactivation itself is the patients module's status change. */
  prompt: z.enum(["reactivate"]).nullable(),
});
export type AppointmentWarning = z.infer<typeof AppointmentWarningSchema>;
/**
 * The patient as an appointment names it (BR-APPOINTMENTS-012, TEN-62, TEN-66): name, account code, status badge, age and
 * photo only — never the date of birth, ID number or medical aid, and no access-log entry for reading it.
 */
export const AppointmentPatientSchema = z.object({
  id: UlidSchema,
  accountCode: z.string(),
  displayName: z.string(),
  isDependant: z.boolean(),
  age: z.number().int(),
  status: z.enum(PATIENT_STATUSES),
  /** Signed, 15 minutes, when the patient has a photo document; null in Sprint 2 (the patients module signs document links). */
  photoUrl: z.string().nullable(),
});
export type AppointmentPatient = z.infer<typeof AppointmentPatientSchema>;

export const AppointmentViewSchema = z.object({
  id: UlidSchema,
  branchId: UlidSchema,
  patientId: UlidSchema,
  patient: AppointmentPatientSchema,
  /** Snapshot of the payer at booking (INT-83). */
  mainAccountIdAtBooking: UlidSchema.nullable(),
  appointmentTypeId: UlidSchema,
  appointmentType: z.object({ id: UlidSchema, code: z.string(), name: z.string(), colourHex: z.string().nullable() }),
  optometristUserId: UlidSchema.nullable(),
  optometrist: UserRefSchema.nullable(),
  startAt: InstantSchema,
  endAt: InstantSchema,
  /** The same instants in the branch's timezone with its offset (`2026-09-10T12:00:00+02:00`), for display (BR-APPOINTMENTS-010). */
  localStartAt: z.string(),
  localEndAt: z.string(),
  durationMinutes: z.number().int(),
  status: z.enum(APPOINTMENT_STATUSES),
  source: z.enum(APPOINTMENT_SOURCES),
  notes: z.string().nullable(),
  roomResource: z.string().nullable(),
  confirmedAt: InstantSchema.nullable(),
  checkedInAt: InstantSchema.nullable(),
  startedAt: InstantSchema.nullable(),
  completedAt: InstantSchema.nullable(),
  cancelledAt: InstantSchema.nullable(),
  cancellationReason: z.string().nullable(),
  noShowAt: InstantSchema.nullable(),
  rescheduledFromId: UlidSchema.nullable(),
  rescheduledToId: UlidSchema.nullable(),
  confirmationSentAt: InstantSchema.nullable(),
  reminderSentAt: InstantSchema.nullable(),
  salesOrderId: UlidSchema.nullable(),
  prescriptionId: UlidSchema.nullable(),
  examinationId: UlidSchema.nullable(),
  /** BR-APPOINTMENTS-003: Bookings still says PENDING. */
  awaitingApproval: z.boolean(),
  /**
   * D63: where to book this patient again — the branch's Zoho Bookings booking page as an Admin typed it on the mapping, for a
   * pulled appointment of a mapped branch ("Rebook"); null when the branch has none, and for a walk-in. Never the generic Zoho
   * app. The booking's own page is `bookings.summaryUrl`.
   */
  bookingsUrl: z.string().nullable(),
  bookings: AppointmentBookingsRefSchema.nullable(),
  /** BR-APPOINTMENTS-016: the service or staff member had no mapping at import. */
  typeUnmapped: z.boolean(),
  staffUnmapped: z.boolean(),
  /** PAT-50/PAT-51/PAT-57: the patient's current status, the banner, and whether staff acknowledged it. */
  patientStatus: z.enum(PATIENT_STATUSES),
  patientStatusWarningAck: z.boolean(),
  warnings: z.array(AppointmentWarningSchema),
  /**
   * BR-GOVERNANCE-027: the patient's consent standing (BR-GOVERNANCE-021), derived from their latest consent record on every read —
   * `None` or `Outdated` is the check-in's prompt to ask for consent, never a block (PAT-74, Q-15). Additive (8 Oct 2026): null in an
   * older answer.
   */
  patientConsentStatus: z.enum(PATIENT_CONSENT_STANDINGS).nullable().default(null),
  /** BR-APPOINTMENTS-002: what a staff action may move the appointment to from here (`Rescheduled` is a pull's move, never listed). */
  allowedTransitions: z.array(z.enum(APPOINTMENT_STATUSES)),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
  audit: AuditStampSchema,
});
export type AppointmentView = z.infer<typeof AppointmentViewSchema>;
export const allowedAppointmentTransitions = (status: AppointmentStatus): readonly AppointmentStatus[] => APPOINTMENT_STATUS_MACHINE.allowedFrom(status);
/** BR-APPOINTMENTS-002: the staff actions' targets — the machine's set without `Rescheduled`, which only a Bookings pull sets. */
export const staffAppointmentTransitions = (status: AppointmentStatus): AppointmentStatus[] => APPOINTMENT_STATUS_MACHINE.allowedFrom(status).filter((s) => s !== "Rescheduled");
/** GET /appointments/{id}/reminders (UC-APPOINTMENTS-014 step 5): the reminder ledger rows of one appointment. */
export const AppointmentReminderViewSchema = z.object({
  id: UlidSchema,
  kind: z.enum(["Confirmation", "Reminder"]),
  offsetMinutes: z.number().int().nullable(),
  scheduledFor: InstantSchema,
  channel: z.enum(["Email", "WhatsApp", "SMS"]).nullable(),
  recipientPatientId: UlidSchema.nullable(),
  recipientValueNormalised: z.string().nullable(),
  consolidationKey: z.string().nullable(),
  status: z.enum(["Pending", "Sent", "Suppressed", "Failed", "Cancelled"]),
  suppressionReason: z.enum(["OptedOut", "ConsentMissing", "PatientDormant", "PatientArchived", "NoChannel", "ConsolidatedIntoOther", "TypeDisabled", "AppointmentChanged"]).nullable(),
  sentAt: InstantSchema.nullable(),
});
export type AppointmentReminderView = z.infer<typeof AppointmentReminderViewSchema>;

/** §5.7 CreateAppointmentInput as Sprint 2 builds it: a walk-in (BR-APPOINTMENTS-001); `staff` and `online` wait for native booking. */
export const CreateAppointmentInputSchema = z.strictObject({
  patientId: UlidSchema,
  optometristId: UlidSchema.nullable().optional(),
  typeId: UlidSchema,
  /** Defaults to now for a walk-in. */
  startAt: InstantSchema.optional(),
  /** Defaults to the type's duration. */
  durationMinutes: Duration.optional(),
  resourceId: optionalText(80),
  notes: optionalText(2000),
  source: z.enum(["walk_in", "staff", "online"]).default("walk_in"),
  /** PAT-50/PAT-51/Q-04: staff saw the Dormant prompt or the Prohibited warning and recorded it anyway. */
  acknowledgePatientStatus: z.boolean().optional(),
}).superRefine((v, ctx) => {
  if (v.source !== "walk_in") ctx.addIssue({ code: "custom", path: ["source"], message: "Zoho Bookings owns the slot: only a walk-in is recorded here (D23)", params: { code: "not_supported" } });
});
export type CreateAppointmentInput = z.infer<typeof CreateAppointmentInputSchema>;
/**
 * PATCH /appointments/{id}: notes and the staff member (UC-APPOINTMENTS-011). Times, type and patient move only in Bookings: the
 * route answers `startAt`, `typeId`, `patientId` and the like with 422 `read_only` before this schema runs (E1), so they are not
 * fields of it.
 */
export const AppointmentPatchSchema = z.strictObject({
  optometristId: UlidSchema.nullable().optional(),
  notes: optionalText(2000),
  acknowledgePatientStatus: z.boolean().optional(),
}).refine((v) => Object.values(v).some((x) => x !== undefined), "nothing to change");
export type AppointmentPatch = z.infer<typeof AppointmentPatchSchema>;
/** The transition bodies: `cancel` takes a reason; the others take an optional time (a back-dated arrival). */
export const AppointmentCancelInputSchema = z.strictObject({ reason: text(500).min(1) });
export const AppointmentTransitionInputSchema = z.strictObject({ at: InstantSchema.optional() });
/**
 * A repeated action answers the current state with `changed: false` (BR-APPOINTMENTS-002). `recallDueDate` is the patient's recall
 * due date as a Complete of a recall-source type left it, a branch-local date (UC-APPOINTMENTS-010 step 2) — null for every other
 * action, a type that is not a recall source and an answer with `changed: false` (BR-APPOINTMENTS-027). Only this answer carries
 * it: AppointmentView, which backs every read, does not. Additive (28 Oct 2026): absent in an older answer.
 */
export const AppointmentTransitionResultSchema = AppointmentViewSchema.extend({ changed: z.boolean(), recallDueDate: LocalDateSchema.nullable().default(null) });
export type AppointmentTransitionResult = z.infer<typeof AppointmentTransitionResultSchema>;
/** §5.7 /reschedule — FEATURE_DISABLED until native booking; the body is kept for that phase. */
export const RescheduleInputSchema = z.strictObject({
  startAt: InstantSchema,
  durationMinutes: Duration,
  optometristId: UlidSchema.nullable().optional(),
  reason: optionalText(500),
});
export type RescheduleInput = z.infer<typeof RescheduleInputSchema>;
export const RescheduleResultSchema = z.object({ newAppointmentId: UlidSchema, originalAppointmentId: UlidSchema });

const rangeCheck = (v: { from?: string | undefined; to?: string | undefined }, ctx: z.RefinementCtx, maxDays: number): void => {
  if (v.from && v.to) {
    const days = (Date.parse(v.to.length === 10 ? `${v.to}T00:00:00.000Z` : v.to) - Date.parse(v.from.length === 10 ? `${v.from}T00:00:00.000Z` : v.from)) / 86_400_000;
    if (days < 0) ctx.addIssue({ code: "custom", path: ["to"], message: "to must not be earlier than from", params: { code: "before_from" } });
    else if (days > maxDays) ctx.addIssue({ code: "custom", path: ["to"], message: `at most ${maxDays} days`, params: { code: "range" } });
  }
};
/** GET /appointments (BR-APPOINTMENTS-012): keyset-paged, start-time order; `branchId` defaults to the acting branch, `all` for Owner/Admin. */
export const AppointmentListQuerySchema = z.object({
  branchId: z.union([UlidSchema, z.literal("all")]).optional(),
  from: LocalDateSchema.optional(),
  to: LocalDateSchema.optional(),
  optometristId: UlidSchema.optional(),
  typeId: UlidSchema.optional(),
  status: z.enum(APPOINTMENT_STATUSES).optional(),
  patientId: UlidSchema.optional(),
  /** Start-time then id; `startAt:desc` for a patient's Appointments tab (PAT-17). */
  sort: z.enum(["startAt:asc", "startAt:desc"]).optional(),
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
}).superRefine((v, ctx) => rangeCheck(v, ctx, APPOINTMENT_LIST_MAX_DAYS));
export type AppointmentListQuery = z.infer<typeof AppointmentListQuerySchema>;

// --- Today and the calendar (BR-APPOINTMENTS-011, -012) -----------------------------------------------------------------------

export const TodayQuerySchema = z.object({ branchId: UlidSchema.optional(), date: LocalDateSchema.optional() });
export type TodayQuery = z.infer<typeof TodayQuerySchema>;
/** BR-APPOINTMENTS-011: `booked = arrived + toCome + noShows` always holds. */
export const TodayFiguresSchema = z.object({
  booked: z.number().int(), arrived: z.number().int(), completed: z.number().int(), inConsultation: z.number().int(),
  waiting: z.number().int(), toCome: z.number().int(), noShows: z.number().int(), cancelled: z.number().int(),
});
export type TodayFigures = z.infer<typeof TodayFiguresSchema>;
export const TodayViewSchema = z.object({
  branchId: UlidSchema,
  date: LocalDateSchema,
  figures: TodayFiguresSchema,
  appointments: z.array(AppointmentViewSchema),
  /** The branch's Unmatched inbox items, so the front desk sees them beside the list. */
  inboxCount: z.number().int(),
  /** When the branch's last Bookings pull finished; null before the first. */
  lastPullAt: InstantSchema.nullable(),
  /**
   * D63: the branch's Zoho Bookings booking page ("Book in Zoho Bookings"), as an Admin typed it on the mapping; null when the
   * branch is unmapped or has none. Additive (23 Sep 2026), like the two fields below: absent in an older answer.
   */
  bookingsUrl: z.string().nullable().default(null),
  /**
   * UC-PATIENTS-011: the dependants-turned-18 worklist lines whose home branch is this branch — dependant children aged 18 or more
   * today in the organisation's timezone, not yet reviewed — counted up to TODAY_COUNT_CAP. The same rule as
   * `GET /patients/dependents-turned-18?branchId=`, which lists them.
   */
  dependentsTurned18: z.number().int().default(0),
  /** Every Active user assigned to the branch, by surname then first name: who a walk-in or an edit may name (BR-APPOINTMENTS-010). */
  assignableStaff: z.array(UserRefSchema).default([]),
});
export type TodayView = z.infer<typeof TodayViewSchema>;
export const CalendarQuerySchema = z.object({
  branchId: UlidSchema.optional(),
  from: LocalDateSchema,
  to: LocalDateSchema,
}).superRefine((v, ctx) => rangeCheck(v, ctx, CALENDAR_MAX_DAYS - 1));
export type CalendarQuery = z.infer<typeof CalendarQuerySchema>;
export const CalendarViewSchema = z.object({
  branchId: UlidSchema,
  from: LocalDateSchema,
  to: LocalDateSchema,
  /** The branch's staff columns: the mapped Bookings staff in mapping order, then anyone else with an appointment in the range; an appointment with no staff member sits in the client's Unassigned column. */
  staff: z.array(UserRefSchema),
  types: z.array(z.object({ id: UlidSchema, code: z.string(), name: z.string(), colourHex: z.string().nullable() })),
  appointments: z.array(AppointmentViewSchema),
  /** BR-APPOINTMENTS-011: the same figures Today shows, per day of the range, so the two screens never disagree. */
  days: z.array(z.object({ date: LocalDateSchema, figures: TodayFiguresSchema })),
  /**
   * Where to book (BR-APPOINTMENTS-001, D63): the branch's Zoho Bookings booking page as an Admin typed it on the mapping; null
   * when the branch is unmapped or has none — never the generic Zoho app.
   */
  bookingsUrl: z.string().nullable(),
  /**
   * Every Active user assigned to the branch, by surname then first name — the staff picker of a walk-in and of an edit
   * (UC-APPOINTMENTS-008, -011), which `staff` (the columns) does not cover for a user with no Bookings mapping and no
   * appointment in the range. Additive (23 Sep 2026): absent in an older answer.
   */
  assignableStaff: z.array(UserRefSchema).default([]),
});
export type CalendarView = z.infer<typeof CalendarViewSchema>;

// --- Recalls (UC-APPOINTMENTS-013; INT-85, rule 34, BR-12) ------------------------------------------------------------------

/** `branchId` defaults to the acting branch, `all` for Owner/Admin (BR-APPOINTMENTS-012). */
export const RecallListQuerySchema = z.object({
  from: LocalDateSchema,
  to: LocalDateSchema,
  branchId: z.union([UlidSchema, z.literal("all")]).optional(),
  typeId: UlidSchema.optional(),
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
}).superRefine((v, ctx) => rangeCheck(v, ctx, 366));
export type RecallListQuery = z.infer<typeof RecallListQuerySchema>;
export const RecallItemSchema = z.object({
  patient: AppointmentPatientSchema,
  dueDate: LocalDateSchema,
  /** The last Completed recall-source appointment, when one exists (a due date may have been imported or seeded without one). */
  sourceAppointmentId: UlidSchema.nullable(),
  typeId: UlidSchema.nullable(),
  typeCode: z.string().nullable(),
  lastCompletedAt: InstantSchema.nullable(),
  /** `RemindersRecalls` consent by channel (rule 34). */
  consent: z.object({ email: z.boolean() }),
  /** A future Scheduled or Confirmed appointment of a recall-source type already exists (UC-APPOINTMENTS-013 A5). */
  alreadyBooked: z.boolean(),
  bookedAppointmentId: UlidSchema.nullable(),
});
export type RecallItem = z.infer<typeof RecallItemSchema>;
export const RecallSnoozeInputSchema = z.strictObject({ until: LocalDateSchema, reason: text(500).min(1) });
export const RecallDismissInputSchema = z.strictObject({ reason: text(500).min(1) });
export const RecallActionResultSchema = z.object({ patientId: UlidSchema, dueDate: LocalDateSchema.nullable(), changed: z.boolean() });
export type RecallActionResult = z.infer<typeof RecallActionResultSchema>;

// --- The Zoho Bookings read adapter (UC-APPOINTMENTS-001..005; D23, D37, D53) ----------------------------------------------

/**
 * D63: a branch's public Zoho Bookings booking page — `https://`, a host, no user name or password, at most
 * BOOKING_PAGE_URL_MAX characters (`https_required`, `invalid_url`, `too_long`). The text itself must start `https://`, not only
 * parse as https: the value is stored as typed and put in an href, where `https:evil.example` (which `new URL` reads as
 * `https://evil.example/`) would resolve against the app's own origin as a path instead.
 */
export const BookingPageUrlSchema = z.string().trim().max(BOOKING_PAGE_URL_MAX).superRefine((v, ctx) => {
  let url: URL | null = null;
  try {
    url = new URL(v);
  } catch {
    url = null;
  }
  if (!url || !url.hostname || url.username || url.password) ctx.addIssue({ code: "custom", message: "the branch's Zoho Bookings booking-page address, starting https://", params: { code: "invalid_url" } });
  else if (url.protocol !== "https:" || !/^https:\/\//i.test(v)) ctx.addIssue({ code: "custom", message: "the booking-page address must start https://", params: { code: "https_required" } });
});
export const BookingsBranchMappingSchema = z.strictObject({
  branchId: UlidSchema,
  workspaceId: z.string().trim().min(1).max(64),
  /** The type an unmapped service imports as; null = the tenant's Follow-up. */
  defaultTypeId: UlidSchema.nullable().optional(),
  /** D63: the branch's booking page, typed by an Admin; null clears it, omitted keeps the saved one. */
  bookingPageUrl: BookingPageUrlSchema.nullable().optional(),
});
export const BookingsStaffMappingSchema = z.strictObject({ userId: UlidSchema, staffId: z.string().trim().min(1).max(64) });
/** PUT /appointments/bookings/mapping: branches ↔ workspaces, users ↔ staff; each id maps once (UC-APPOINTMENTS-001 step 3). */
export const BookingsMappingSchema = z.strictObject({
  branches: z.array(BookingsBranchMappingSchema).max(50),
  staff: z.array(BookingsStaffMappingSchema).max(200),
}).superRefine((v, ctx) => {
  const dup = (values: string[]) => values.find((x, i) => values.indexOf(x) !== i);
  if (dup(v.branches.map((b) => b.branchId))) ctx.addIssue({ code: "custom", path: ["branches"], message: "a branch maps to one workspace", params: { code: "duplicate" } });
  if (dup(v.branches.map((b) => b.workspaceId))) ctx.addIssue({ code: "custom", path: ["branches"], message: "a workspace maps to one branch", params: { code: "duplicate" } });
  if (dup(v.staff.map((s) => s.userId))) ctx.addIssue({ code: "custom", path: ["staff"], message: "a user maps to one staff id", params: { code: "duplicate" } });
  if (dup(v.staff.map((s) => s.staffId))) ctx.addIssue({ code: "custom", path: ["staff"], message: "a staff id maps to one user", params: { code: "duplicate" } });
});
export type BookingsMapping = z.infer<typeof BookingsMappingSchema>;
export const BookingsMappingViewSchema = z.object({
  /** `bookingPageUrl` (D63): the booking page an Admin typed for the branch, null until one is; additive (23 Sep 2026), absent in an older answer. */
  branches: z.array(z.object({ branchId: UlidSchema, branchCode: z.string(), branchName: z.string(), workspaceId: z.string(), workspaceName: z.string().nullable(), defaultTypeId: UlidSchema.nullable(), bookingPageUrl: z.string().nullable().default(null) })),
  staff: z.array(z.object({ userId: UlidSchema, displayName: z.string(), staffId: z.string() })),
  updatedAt: InstantSchema.nullable(),
  /** When the last run finished, per branch. */
  lastRuns: z.array(z.object({ branchId: UlidSchema, runId: UlidSchema, finishedAt: InstantSchema.nullable(), outcome: z.enum(BOOKINGS_SYNC_OUTCOMES) })),
  /** UC-APPOINTMENTS-001 A2: the three Zoho secrets are on the Worker; without them the live listing and the pull answer PROVIDER_NOT_READY. */
  credentialsPresent: z.boolean(),
});
export type BookingsMappingView = z.infer<typeof BookingsMappingViewSchema>;
/** GET /appointments/bookings/workspaces: the organisation's Bookings workspaces, live, with their staff and services. */
export const BookingsWorkspacesViewSchema = z.object({
  workspaces: z.array(z.object({
    id: z.string(),
    name: z.string(),
    timezone: z.string().nullable(),
    /** `suggestedUserId`: the organisation's user with the same email, offered as the pairing (UC-APPOINTMENTS-001 step 2). */
    staff: z.array(z.object({ id: z.string(), name: z.string(), email: z.string().nullable(), suggestedUserId: UlidSchema.nullable() })),
    services: z.array(z.object({ id: z.string(), name: z.string(), durationMinutes: z.number().int().nullable() })),
  })),
  fetchedAt: InstantSchema,
});
export type BookingsWorkspacesView = z.infer<typeof BookingsWorkspacesViewSchema>;

/** POST /appointments/bookings/sync: pull one branch now over the near window, or the deep window with `full` (BR-APPOINTMENTS-007). */
export const BookingsSyncRequestSchema = z.strictObject({
  branchId: UlidSchema,
  full: z.boolean().optional(),
});
export type BookingsSyncRequest = z.infer<typeof BookingsSyncRequestSchema>;
export const BookingsRunCountsSchema = z.object(Object.fromEntries(BOOKINGS_RUN_COUNT_FIELDS.map((k) => [k, z.number().int()])) as Record<(typeof BOOKINGS_RUN_COUNT_FIELDS)[number], z.ZodNumber>);
export type BookingsRunCounts = z.infer<typeof BookingsRunCountsSchema>;
export const BookingsSyncRunViewSchema = z.object({
  id: UlidSchema,
  branchId: UlidSchema,
  trigger: z.enum(BOOKINGS_SYNC_TRIGGERS),
  startedBy: UserRefSchema.nullable(),
  windowKind: z.enum(BOOKINGS_WINDOW_KINDS),
  windowFrom: InstantSchema,
  windowTo: InstantSchema,
  startedAt: InstantSchema,
  finishedAt: InstantSchema.nullable(),
  outcome: z.enum(BOOKINGS_SYNC_OUTCOMES),
  counts: BookingsRunCountsSchema,
  errorText: z.string().nullable(),
});
export type BookingsSyncRunView = z.infer<typeof BookingsSyncRunViewSchema>;
/** POST /appointments/bookings/sync answers the run — the one it started, or the one already running or just finished (`started: false`). */
export const BookingsSyncResponseSchema = BookingsSyncRunViewSchema.extend({ started: z.boolean() });
export type BookingsSyncResponse = z.infer<typeof BookingsSyncResponseSchema>;
/** `branchId` defaults to the acting branch, `all` for Owner/Admin (BR-APPOINTMENTS-012). */
export const BookingsSyncRunsQuerySchema = z.object({
  branchId: z.union([UlidSchema, z.literal("all")]).optional(),
  outcome: z.enum(BOOKINGS_SYNC_OUTCOMES).optional(),
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
});
export type BookingsSyncRunsQuery = z.infer<typeof BookingsSyncRunsQuerySchema>;

/** A candidate the inbox offers, with what matched (BR-APPOINTMENTS-006); `current` marks the patient a changed booking is still on. */
export const BookingsInboxCandidateSchema = AppointmentPatientSchema.extend({ matchedOn: z.array(z.enum(["name", "email", "mobile", "current"])) });
/** The create-patient pre-fill (UC-APPOINTMENTS-004 step 4): the name split at the last space, the phone in the `0…` form. */
export const BookingsInboxSuggestedPatientSchema = z.object({ surname: z.string(), firstNames: z.string(), email: z.string().nullable(), mobile: z.string().nullable() });
export const BookingsInboxItemSchema = z.object({
  id: UlidSchema,
  branchId: UlidSchema,
  bookingId: z.string(),
  workspaceId: z.string(),
  staffId: z.string().nullable(),
  staffName: z.string().nullable(),
  customerName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  customerMobile: z.string().nullable(),
  startAt: InstantSchema,
  endAt: InstantSchema,
  serviceName: z.string().nullable(),
  remoteStatus: z.string().nullable(),
  reason: z.enum(BOOKINGS_INBOX_REASONS),
  candidates: z.array(BookingsInboxCandidateSchema),
  suggestedPatient: BookingsInboxSuggestedPatientSchema,
  status: z.enum(BOOKINGS_INBOX_STATUSES),
  patientId: UlidSchema.nullable(),
  appointmentId: UlidSchema.nullable(),
  resolvedBy: UserRefSchema.nullable(),
  resolvedAt: InstantSchema.nullable(),
  resolutionReason: z.string().nullable(),
  firstSeenAt: InstantSchema,
  lastSeenAt: InstantSchema,
  createdAt: InstantSchema,
});
export type BookingsInboxItem = z.infer<typeof BookingsInboxItemSchema>;
/** `branchId` defaults to the acting branch, `all` for Owner/Admin (BR-APPOINTMENTS-012). */
export const BookingsInboxQuerySchema = z.object({
  status: z.enum(BOOKINGS_INBOX_STATUSES).optional(),
  branchId: z.union([UlidSchema, z.literal("all")]).optional(),
  from: LocalDateSchema.optional(),
  to: LocalDateSchema.optional(),
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
}).superRefine((v, ctx) => rangeCheck(v, ctx, APPOINTMENT_LIST_MAX_DAYS));
export type BookingsInboxQuery = z.infer<typeof BookingsInboxQuerySchema>;
/** POST /appointments/bookings/inbox/{id}/link: the patient it is; the link is remembered for that contact (D37). */
export const InboxLinkInputSchema = z.strictObject({ patientId: UlidSchema });
/** POST /appointments/bookings/inbox/{id}/ignore: never imported; the reason is recorded. */
export const InboxIgnoreInputSchema = z.strictObject({ reason: text(500).min(1) });
/** POST /appointments/bookings/inbox/{id}/create-patient: the pre-filled create body, completed by the receptionist (also needs patients.patient.write). */
export const InboxCreatePatientInputSchema = CreatePatientInputSchema;
export const InboxResolutionResultSchema = z.object({
  inboxItemId: UlidSchema,
  status: z.enum(BOOKINGS_INBOX_STATUSES),
  patientId: UlidSchema.nullable(),
  appointmentId: UlidSchema.nullable(),
});
export type InboxResolutionResult = z.infer<typeof InboxResolutionResultSchema>;

export interface BookingsMatchKeys { nameNormalised: string; emailNormalised: string | null; mobileDigits: string | null }
/** D37's keys, folded like the patient's own (BR-16): the name exactly, then the email or the mobile. */
export function bookingsMatchKeys(customer: { name: string; email?: string | null | undefined; mobile?: string | null | undefined }): BookingsMatchKeys {
  return {
    nameNormalised: foldName(customer.name),
    emailNormalised: customer.email ? normaliseEmail(customer.email) : null,
    mobileDigits: customer.mobile ? normaliseMobile(customer.mobile)?.digits ?? null : null,
  };
}
/** The remembered-link key (BR-APPOINTMENTS-005): the three normalised parts together, empty when absent. */
export const bookingsContactKey = (k: BookingsMatchKeys): string => `${k.nameNormalised}|${k.emailNormalised ?? ""}|${k.mobileDigits ?? ""}`;
/**
 * Whether a remembered link may match a NEW booking of this customer (BR-APPOINTMENTS-005): only when the booking carries an
 * email or a mobile. A name alone is not D37's match, so a new contact-less booking always waits in the inbox; the link the
 * desk made for such a booking still keeps that booking's own appointment with its patient on later pulls.
 */
export const bookingsContactRememberable = (k: BookingsMatchKeys): boolean => k.emailNormalised !== null || k.mobileDigits !== null;
/**
 * BR-APPOINTMENTS-005: the customer name equals the patient's first names and surname in either order, and the email or the
 * mobile equals the patient's effective one (own, or the main account's when the patient uses its contact details).
 */
export function bookingMatchesPatient(booking: BookingsMatchKeys, patient: { firstNames: string; surname: string; emailNormalised: string | null; mobileDigits: string | null }): { name: boolean; email: boolean; mobile: boolean; matches: boolean } {
  const name = booking.nameNormalised.length > 0 && (booking.nameNormalised === foldName(`${patient.firstNames} ${patient.surname}`) || booking.nameNormalised === foldName(`${patient.surname} ${patient.firstNames}`));
  const email = booking.emailNormalised !== null && booking.emailNormalised === patient.emailNormalised;
  const mobile = booking.mobileDigits !== null && booking.mobileDigits === patient.mobileDigits;
  return { name, email, mobile, matches: name && (email || mobile) };
}
